// src/components/innervoice/InnerVoiceModulesIntro.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import {
  Play,
  Brain,
  Target,
  Award,
  ArrowRight,
  CheckCircle,
  Zap,
  Heart,
  Users,
  Lightbulb,
  TrendingUp,
  Crown,
  Compass,
  Shield
} from 'lucide-react';

const InnerVoiceModulesIntro = () => {
  const { setCurrentView } = useApp();
  const { user } = usePrivacyAuth();

  // Inner Voice Vibe Modules Data
  const modulesData = [
    {
      id: 1,
      title: 'The Neuroscience of Self-Talk',
      subtitle: "Your Brain's Hidden Operating System",
      description: 'Discover how your inner voice literally rewires your brain and shapes your reality through cutting-edge neuroscience research.',
      image: '/images/tess-mod01-720px.png',
      icon: Brain,
      duration: '25 min',
      level: 'Foundation'
    },
    {
      id: 2,
      title: 'Advanced Cognitive Restructuring',
      subtitle: 'The Art and Science of Thought Transformation',
      description: 'Master the advanced techniques for identifying and transforming limiting thought patterns into empowering beliefs.',
      image: '/images/tess-mod02-720px.png',
      icon: Target,
      duration: '30 min',
      level: 'Core Skills'
    },
    {
      id: 3,
      title: 'Emotional Regulation Mastery',
      subtitle: 'Advanced Self-Talk for Emotional Intelligence',
      description: 'Learn to navigate complex emotions with sophisticated self-talk strategies for peak emotional intelligence.',
      image: '/images/tess-mod03-720px.png',
      icon: Heart,
      duration: '28 min',
      level: 'Core Skills'
    },
    {
      id: 4,
      title: 'Social Confidence Architecture',
      subtitle: 'Building Authentic Self-Assurance in Social Contexts',
      description: 'Develop unshakeable social confidence through strategic inner dialogue and authentic presence techniques.',
      image: '/images/tess-mod04-720px.png',
      icon: Users,
      duration: '32 min',
      level: 'Advanced'
    },
    {
      id: 5,
      title: 'Future Self Visioning',
      subtitle: 'Architecting Your Ideal Identity Through Strategic Self-Talk',
      description: 'Design and embody your future self using advanced mental time travel and identity transformation techniques.',
      image: '/images/tess-mod05-720px.png',
      icon: Compass,
      duration: '35 min',
      level: 'Advanced'
    },
    {
      id: 6,
      title: 'Identity & Self-Worth Foundations',
      subtitle: 'Building Unshakeable Self-Esteem Through Core Identity Work',
      description: 'Establish rock-solid self-worth foundations that remain stable regardless of external circumstances.',
      image: '/images/tess-mod06-720px.png',
      icon: Crown,
      duration: '38 min',
      level: 'Advanced'
    },
    {
      id: 7,
      title: 'Advanced Resilience & Recovery',
      subtitle: 'Crisis-Proofing Your Mental Health Through Strategic Preparation',
      description: 'Build antifragile mental health systems that help you bounce back stronger from any challenge.',
      image: '/images/tess-mod07-720px.png',
      icon: Shield,
      duration: '33 min',
      level: 'Mastery'
    },
    {
      id: 8,
      title: 'Advanced Integration & Mastery',
      subtitle: 'Synthesising All Elements into a Personal Self-Talk Mastery System',
      description: 'Integrate all learning into your personalised Self-Talk Mastery System for lifelong transformation.',
      image: '/images/tess-mod08-720px.png',
      icon: Award,
      duration: '40 min',
      level: 'Mastery'
    }
  ];

  const ecosystemPillars = [
    {
      title: 'Take Your Assessment',
      subtitle: 'Discover Your Inner Voice Patterns',
      description: 'Get personalized insights into your self-talk patterns, emotional triggers, and mental frameworks. Our science-backed assessment reveals exactly where to focus your transformation efforts.',
      icon: Brain,
      iconColor: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      buttonText: 'Start Assessment',
      buttonAction: () => setCurrentView('vibe-assessment-intro')
    },
    {
      title: 'Dive Into Learning',
      subtitle: 'Master Your Mental Architecture',
      description: 'Eight comprehensive modules guide you through neuroscience-backed techniques for transforming your inner dialogue. From foundational concepts to advanced mastery.',
      icon: Target,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
      buttonText: 'Explore Modules',
      buttonAction: () => setCurrentView('modules')
    },
    {
      title: 'Your Transformation Lab',
      subtitle: 'Practice & Integration Tools',
      description: 'Interactive exercises, reflection prompts, and progress tracking tools that help you integrate insights into daily life for lasting transformation.',
      icon: Lightbulb,
      iconColor: 'text-orange-300',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
      buttonText: 'Access Tools',
      buttonAction: () => setCurrentView('tools')
    }
  ];

  const benefits = [
    'Transform limiting beliefs into empowering narratives',
    'Develop unshakeable emotional resilience',
    'Master the neuroscience of positive mindset',
    'Build authentic self-confidence from within',
    'Create your future self through strategic thinking',
    'Establish rock-solid self-worth foundations',
    'Develop crisis-proof mental health systems',
    'Integrate all skills into a personal mastery system'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-purple-200 text-sm font-medium mb-6">
              <Zap className="w-4 h-4 mr-2" />
              Transform Your Inner Voice • Build Mental Mastery
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Master Your
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Inner Voice</span>
            </h1>

            <p className="text-xl text-purple-200 mb-8 leading-relaxed">
              Transform your self-talk from your biggest critic into your most powerful ally.
              Eight neuroscience-backed modules guide you through complete inner dialogue transformation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentView('vibe-assessment-intro')}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Your Inner Voice Assessment
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentView('modules')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all flex items-center justify-center"
              >
                Preview Learning Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Three Pillars Section */}
      <div className="py-20 bg-black/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Your Complete Inner Voice Transformation System
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Three integrated components work together to create lasting change in your mental patterns and emotional wellbeing.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {ecosystemPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                className={`${pillar.bgColor} backdrop-blur-sm border ${pillar.borderColor} rounded-2xl p-8 text-center hover:scale-105 transition-transform`}
              >
                <div className={`w-16 h-16 ${pillar.bgColor} border ${pillar.borderColor} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <pillar.icon className={`w-8 h-8 ${pillar.iconColor}`} />
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">{pillar.title}</h3>
                <h4 className="text-lg font-semibold text-purple-300 mb-4">{pillar.subtitle}</h4>
                <p className="text-purple-200 mb-6 leading-relaxed">{pillar.description}</p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={pillar.buttonAction}
                  className="w-full px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                >
                  {pillar.buttonText}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Modules Preview Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Your 8-Module Learning Journey
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              From neuroscience foundations to advanced mastery - each module builds upon the last to create complete inner voice transformation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {modulesData.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:scale-105 transition-transform group"
              >
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={module.image}
                    alt={module.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-purple-600/80 backdrop-blur-sm text-white text-xs font-semibold rounded-full mb-2">
                      {module.level}
                    </span>
                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full ml-2">
                      {module.duration}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <module.icon className="w-5 h-5 text-purple-400 mr-2" />
                    <span className="text-sm font-medium text-purple-300">Module {module.id}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">{module.title}</h3>
                  <p className="text-sm text-yellow-300 mb-3">{module.subtitle}</p>
                  <p className="text-xs text-purple-200 leading-relaxed">{module.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentView('modules')}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center mx-auto"
            >
              Enter Your Learning Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-black/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Transform Every Aspect of Your Inner World
              </h2>
              <p className="text-xl text-purple-200">
                Master the mental skills that create lasting change in your life, relationships, and potential.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 + index * 0.1 }}
                  className="flex items-center p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="text-center mt-12"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentView('vibe-assessment-intro')}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center mx-auto"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                Begin Your Transformation Journey
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InnerVoiceModulesIntro;